<template>
  <div class="text-primary-1" :class="{ 'mb-20': isSupplier }">
    <h2
      v-if="!isSupplier && !isGeneral"
      class="text-mobile-heading-2 md:text-heading-2"
    >
      {{ t("discover.allActivities") }}
    </h2>
    <div
      class="relative max-md:flex max-md:flex-row-reverse max-md:items-center max-md:justify-between"
      :class="{ 'max-md:!block': isSupplier }"
    >
      <div
        class="md:flex justify-between mt-5 max-md:mb-5"
        :class="{ 'max-md:!flex max-md:!mb-[12px]': isSupplier }"
      >
        <div class="flex gap-3">
          <div
            class="filter-button"
            :class="{
              'max-md:!relative  max-md:!bottom-auto': isSupplier,
            }"
            @click="showFilterModal = true"
          >
            <span v-if="filterLength()" class="filter-length">
              {{ filterLength() }}
            </span>
            <icon-filter-list class="!text-primary-1 text-[22px] !mb-0" />
            <span>{{ t("discover.filter") }}</span>
          </div>
          <BaseButton
            v-if="getQuery().length > 0"
            class="max-md:!hidden !rounded-full !h-11 !ps-5 !pe-4 !py-2.5 !border-[1px] !border-gray-2 [&_svg]:rtl:mr-0.5 [&_svg]:ltr:ml-0.5"
            type="outlined"
            :label="t('discover.resetAll')"
            font="md:!text-heading-6 !text-mobile-heading-6"
            hover-background-color="white"
            hover-color="primary-2"
            color="gray-2"
            append-icon="IconRestart"
            @click="resetQuery"
          />
        </div>
        <LazyDiscoverActivitiesSort
          class="flex gap-3"
          @change-sort="changeSort"
        />
      </div>
      <p
        v-if="!loading"
        class="text-mobile-paragraph-1 md:text-paragraph-1 md:mb-2.5 md:mt-5 max-md:top-0"
        :class="{
          'max-md:!mb-5': isSupplier,
        }"
      >
        {{ totalProduct }} {{ t("discover.resultsFound") }}.
      </p>
    </div>
    <BaseButton
      v-if="getQuery().length > 0"
      class="[&_.append-icon]:!text-[20px] md:!hidden !rounded-full !h-9 mb-5 !ps-5 !pe-4 !py-2.5 !border-[1px] !border-gray-2 [&_svg]:rtl:mr-0.5 [&_svg]:ltr:ml-0.5"
      type="outlined"
      :label="t('discover.resetAll')"
      hover-background-color="white"
      hover-color="primary-2"
      color="gray-2"
      font="md:!text-heading-6 !text-mobile-heading-6"
      append-icon="IconRestart"
      @click="resetQuery"
    />
    <div
      v-if="discoverProducts.length && !isSupplier"
      class="grid gap-[15px] md:gap-[26px] max-md:hidden"
      :class="layoutSize < 1000 ? 'md:grid-cols-3' : 'md:grid-cols-4'"
    >
      <LazyExperienceCardHandler
        v-for="item in discoverProducts"
        :key="item.id"
        :card-type="discoverProductCardType"
        :dark-mode="darkMode"
        :item="item"
        :vertical="true"
      />
    </div>
    <div
      v-if="discoverProducts.length && isSupplier && discoverProductCardType"
      class="grid gap-[15px] md:gap-[26px] max-md:hidden"
      :class="layoutSize < 1000 ? 'md:grid-cols-3' : 'md:grid-cols-4'"
    >
      <ExperienceCardHandler
        v-for="item in discoverProducts"
        :key="item.id"
        :card-type="discoverProductCardType"
        :dark-mode="darkMode"
        :item="item"
        :vertical="true"
      />
    </div>
    <div
      v-if="discoverProducts.length && isSupplier && !discoverProductCardType"
      class="max-md:hidden"
    >
      <ExperienceCardThree
        v-for="item in discoverProducts"
        :id="item?.id"
        :key="item?.id"
        :image="item?.photo_url ?? item?.thumbnail"
        :cities="item?.cities"
        :title="item?.title"
        :categories="item?.categories"
        :short-description="item?.short_description"
        :rate="item?.rating"
        :market-price="item?.market_price?.amount || 0"
        :price="item?.starting_price?.amount || 0"
        :currency="item?.starting_price?.currency.code || '-'"
        :slug="item?.slug"
        :is-negotiable="item?.is_negotiable"
        :has-pay-later="item?.payment_options?.cash_payment"
        class="!max-w-[1500px] !block mb-5"
        :to-path="productRoute(item)"
      />
    </div>
    <div
      v-if="discoverProducts.length"
      class="flex flex-col gap-[24px] md:gap-[30px] md:hidden"
    >
      <LazyExperienceCardXs
        v-for="item in discoverProducts"
        :id="item?.id"
        :key="item?.id"
        :image="item?.photo_url ?? item?.thumbnail"
        :cities="isDesktop ? item?.cities : []"
        :title="item?.title"
        :categories="item?.categories"
        :rate="item?.rating"
        :market-price="item?.market_price?.amount || 0"
        :price="item?.starting_price?.amount || 0"
        :currency="item?.starting_price?.currency.code || '-'"
        :slug="item?.slug"
        :is-negotiable="item?.is_negotiable"
        :to-path="productRoute(item)"
        :has-pay-later="item?.payment_options?.cash_payment"
      />
    </div>
    <div
      v-else-if="!loading"
      class="w-full py-24 border-curve bg-white text-center flex flex-col items-center justify-center px-5"
    >
      <NuxtImg
        loading="lazy"
        src="/no-result.svg"
        class="md:w-[50px] h-[50px]"
      />
      <span
        class="text-primary-1 md:text-heading-1 text-mobile-heading-1 mt-0.5"
      >
        {{ t("discover.noResultsFound") }}
      </span>
      <span
        class="md:text-paragraph-1 text-mobile-paragraph-1 text-gray-1 mt-5"
      >
        {{ t("discover.noResultsDescription") }}
      </span>
    </div>
    <div
      v-if="loading && !isSupplier"
      class="grid gap-[15px] md:gap-[26px] mt-[15px] md:mt-[26px]"
      :class="type == 3 ? '' : 'grid-cols-1 md:grid-cols-4'"
    >
      <LazyExperienceCardSkeletonLoading
        v-for="item in 8"
        :key="item"
        :card-type="type"
      />
    </div>
    <div v-if="currentPage < totalPages" class="text-center mt-[58px]">
      <BaseButton
        type="third"
        color="primary-2"
        :loading="loading"
        hover-background-color="primary-2"
        class="z-10 !text-link-1 !h-[49px] min-w-[233px] max-md:!w-full"
        hover-color="white"
        :label="t('discover.loadMoreExperiences')"
        @click="loadMore"
      />
    </div>
    <BaseSimpleModal
      v-if="isDesktop"
      v-model:modal-show="showFilterModal"
      custom-class="!overflow-y-hidden !w-[598px] !bg-gray-5 !py-5"
    >
      <template #header>
        <div class="modal-header">
          {{ t("explore.filterBy") }}
        </div>
      </template>
      <template #content>
        <LazyDiscoverFilter
          :facets="facets"
          :cities="cities"
          @select-city="selectedCity"
          @reset-all="resetAll"
          @show-result="showFilterModal = false"
          @scroll-to-activities="emit('scroll-to-activities')"
        />
      </template>
    </BaseSimpleModal>
    <BaseBottomSheet v-else v-model:show="showFilterModal" fit-height>
      <template #header>
        <div class="flex items-center justify-center w-full -mt-3 py-4">
          <IconClose
            class="text-gray-1 text-[14px] cursor-pointer me-auto"
            @click="showFilterModal = false"
          />
          <span
            class="text-primary-1 md:text-heading-4 text-mobile-heading-4 me-auto"
          >
            {{ t("explore.filterBy") }}
          </span>
        </div>
      </template>
      <LazyDiscoverFilter
        :facets="facets"
        :cities="cities"
        @reset-all="resetAll"
        @show-result="showFilterModal = false"
        @scroll-to-activities="emit('scroll-to-activities')"
      />
    </BaseBottomSheet>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type {
  IOptions,
  IProduct,
  IApiResponse,
  ICity,
} from "../../types/general";
import { useRoute, useRouter } from "vue-router";
import useCustomFetch from "../../composables/useCustomFetch";
const layoutSize = ref();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const emit = defineEmits(["scroll-to-activities"]);
onMounted(() => {
  const rootStyles = getComputedStyle(document.documentElement);
  let tmp = rootStyles.getPropertyValue("--layout-max-width").trim();
  layoutSize.value = parseInt(tmp);
});
const props = defineProps({
  facets: {
    type: Object,
    required: true,
  },
  isSupplier: { type: Boolean, default: false },
  isGeneral: { type: Boolean, default: false },
  darkMode: {
    type: Boolean,
    default: false,
  },
});
const localePath = useLocalePath();
function productRoute(item: IProduct) {
  if (props.isSupplier) {
    return localePath(`/experiences/${item.slug}`);
  } else {
    return localePath(`/things-to-do-in-${item.cities[0].slug}/${item.slug}`);
  }
}
const option = ref<IOptions>({
  id: 1,
  title: t("discover.recommended"),
});
const config = useRuntimeConfig();
const type = (config.public.statics as any)?.productCardType;
const discoverProductCardType = (config.public.statics as any)
  ?.discoverProductCardType
  ? (config.public.statics as any)?.discoverProductCardType
  : type;
const productsPerPege = (config.public.statics as any)?.discoverProductsPerPege
  ? (config.public.statics as any)?.discoverProductsPerPege
  : 8;
const loading = ref<boolean>(true);
const currentPage = ref(1);
const totalPages = ref(2);
const totalProduct = ref(0);
const discoverProducts = ref<IProduct[]>([]);
const computedLoading = computed(() => {
  return discoverProducts.value.length == 0 ? 8 : discoverProducts.value.length;
});
async function loadMore() {
  await fetchProducts(currentPage.value + 1);
}
function changeSort(val) {
  option.value = val;
}
const city = ref<String>();
async function fetchProducts(page?: number) {
  loading.value = true;
  const { data, fetchData } = useCustomFetch(
    `/api/v2/products?page=${page ?? 1}&per_page=${productsPerPege}&sort=${sort.value}&${getQuery()}&${!props.isSupplier && !props.isGeneral ? `filter[city]=${route.params.slug}&` : route.params.categorySlug && route.params.categorySlug != "things-to-do" ? `filter[category]=${route.params.categorySlug}` : ""}`
  );
  await fetchData();
  const productTmp: IApiResponse<IProduct[]> | null =
    data.value as IApiResponse<IProduct[]> | null;
  discoverProducts.value = [
    ...discoverProducts.value,
    ...(productTmp?.data ?? []),
  ];
  totalPages.value = productTmp?.meta?.last_page ?? 1;
  totalProduct.value = productTmp?.meta?.total ?? 0;
  currentPage.value = page ?? 1;
  loading.value = false;
}

const sort = ref<string>("");
watch(option, async (newVal) => {
  if (newVal.id == 1) sort.value = "";
  else if (newVal.id == 2) sort.value = "price";
  else if (newVal.id == 3) sort.value = "-price";
  currentPage.value = 1;
  discoverProducts.value = [];
  loading.value = true;
  await fetchProducts();
});

watch(
  route,
  async () => {
    currentPage.value = 1;
    discoverProducts.value = [];
    loading.value = true;
    await fetchProducts();
  },
  { deep: true, immediate: true }
);

function getQuery() {
  let queryTemp = "";
  if (Object.keys(route.query).length) {
    if (route.query.category) {
      queryTemp = `${queryTemp}filter[category]=${route.query.category}&`;
    }
    if (route.query.city) {
      queryTemp = `${queryTemp}filter[city]=${route.query.city}&`;
    }
    if (route.query.labels) {
      queryTemp = `${queryTemp}filter[label]=${route.query.labels}&`;
    }
    if (route.query["best-time"]) {
      queryTemp = `${queryTemp}filter[attribute][best-time]=${route.query["best-time"]}&`;
    }
    if (route.query["duration"]) {
      queryTemp = `${queryTemp}filter[attribute][duration]=${route.query["duration"]}&`;
    }
    if (route.query["perfect-for"]) {
      queryTemp = `${queryTemp}filter[attribute][perfect-for]=${route.query["perfect-for"]}&`;
    }
    if (route.query["age-range"]) {
      queryTemp = `${queryTemp}filter[attribute][age-range]=${route.query["age-range"]}&`;
    }
    if (route.query["transport"]) {
      queryTemp = `${queryTemp}filter[attribute][transport]=${route.query["transport"]}&`;
    }
    if (route.query["occasion"]) {
      queryTemp = `${queryTemp}filter[attribute][occasion]=${route.query["occasion"]}&`;
    }
    if (route.query["languages"]) {
      queryTemp = `${queryTemp}filter[attribute][languages]=${route.query["languages"]}&`;
    }
    if (route.query.rate) {
      queryTemp = `${queryTemp}filter[rating]=${route.query.rate}&`;
    }
    if (route.query.capacity) {
      queryTemp = `${queryTemp}filter[capacity]=${route.query.capacity}&`;
    }
    if (route.query.price_range) {
      const priceRange = route.query.price_range as string;
      const [minPrice, maxPrice] = priceRange.split("-");
      queryTemp = `${queryTemp}filter[min_price]=${minPrice}&filter[max_price]=${maxPrice}&`;
    }
  }
  return queryTemp;
}

const resetQuery = () => {
  router.push({ query: {} });
};

const isDesktop = ref(false);
const showFilterModal = ref(false);
const handleResize = () => {
  isDesktop.value = window.innerWidth >= 768;
};
onMounted(() => {
  handleResize();
});
const resetAll = () => {
  resetQuery();
  showFilterModal.value = false;
};
function filterLength() {
  const query = route.query;
  let count = 0;

  if (query["best-time"]) {
    const attributes = Array.isArray(query["best-time"])
      ? query["best-time"].join(",")
      : query["best-time"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["duration"]) {
    const attributes = Array.isArray(query["duration"])
      ? query["duration"].join(",")
      : query["duration"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["perfect-for"]) {
    const attributes = Array.isArray(query["perfect-for"])
      ? query["perfect-for"].join(",")
      : query["perfect-for"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["age-range"]) {
    const attributes = Array.isArray(query["age-range"])
      ? query["age-range"].join(",")
      : query["age-range"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["transport"]) {
    const attributes = Array.isArray(query["transport"])
      ? query["transport"].join(",")
      : query["transport"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["occasion"]) {
    const attributes = Array.isArray(query["occasion"])
      ? query["occasion"].join(",")
      : query["occasion"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query["languages"]) {
    const attributes = Array.isArray(query["languages"])
      ? query["languages"].join(",")
      : query["languages"];
    count += attributes ? attributes.split(",").length : 0;
  }
  if (query.labels) {
    const labels = Array.isArray(query.labels)
      ? query.labels.join(",")
      : query.labels;
    count += labels ? labels.split(",").length : 0; // Count labels
  }
  if (query.price_range) {
    count += 1; // Count price range as one filter
  }
  if (query.city) {
    count += 1; // Count price range as one filter
  }
  if (query.rate) {
    count += 1; // Count rating as one filter
  }
  if (query.category) {
    const category = Array.isArray(query.category)
      ? query.category.join(",")
      : query.category;
    count += category ? category.split(",").length : 0; // Count labels
  }

  return count;
}
const cities = ref<ICity[] | []>([]);

getCities();
async function getCities() {
  const { data: responseData, fetchData: fetchCities } =
    useCustomFetch("/api/cities");
  // const cities = ref<ICity[] | []>([]);
  let tmp: ICity[] | [] = [];

  await fetchCities();
  const tmpData: IApiResponse<ICity[]> | null =
    responseData.value as IApiResponse<ICity[]> | null;
  tmp = tmpData?.data ?? [];
  cities.value = tmp;
}
function selectedCity(val: String) {
  city.value = val;
}
</script>
<style lang="scss" scoped>
.filter-button {
  @apply text-primary-1 hover:bg-gray-3 relative md:bg-gray-4 bg-gray-5 rounded-[40px] h-[42px] md:h-11 px-[18px] md:px-5 flex gap-1.5 items-center cursor-pointer 
  max-md:fixed max-md:bottom-5 max-md:left-1/2 max-md:-translate-x-1/2 max-md:z-20 border border-gray-3;
  .filter-length {
    @apply bg-primary-1 text-[11px] text-white absolute right-2 -top-0.5 w-[24px] h-[14px] rounded-[30px] px-1.5 py-0.5 flex items-center text-center justify-center;
  }
}
.modal-header {
  @apply w-full text-center -mt-3 border-b border-gray-2 text-primary-1 md:text-heading-4 text-mobile-heading-4 pb-4;
}
.dark-mode {
  h2,
  p {
    @apply text-title-color;
  }
}
</style>
